import type { Transition } from "motion/react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";

/** Instant transition for reduced motion or disabled animations. */
export const UI_MOTION_INSTANT_TRANSITION: Transition = { "duration": 0 };

export type UiMotionSpringPreset = keyof typeof UI_MOTION.spring;

/**
 * Resolves a spring transition or instant fallback when motion should be suppressed.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @param preset Spring preset key from {@link UI_MOTION.spring}.
 * @returns Motion transition object.
 */
export const resolveUiSpring = (
    reducedMotion: boolean,
    preset: UiMotionSpringPreset = "default",
): Transition => {
    if ( reducedMotion || !UI_MOTION.enabled ) {
        return UI_MOTION_INSTANT_TRANSITION;
    }

    return UI_MOTION.spring[preset];
};

/**
 * Resolves an ease-out transition for dismiss paths.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @param durationMs Duration in milliseconds.
 * @returns Motion transition object.
 */
export const resolveUiEaseOut = (
    reducedMotion: boolean,
    durationMs: number = UI_MOTION.duration.fastMs,
): Transition => {
    if ( reducedMotion || !UI_MOTION.enabled ) {
        return UI_MOTION_INSTANT_TRANSITION;
    }

    return {
        "duration": durationMs / 1000,
        "ease":     UI_MOTION.easingIos,
    };
};

/**
 * Computes stagger delay in seconds for a list item index.
 *
 * @param index Zero-based item index.
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Delay in seconds for Motion `transition.delay`.
 */
export const resolveUiStagger = ( index: number, reducedMotion: boolean ): number => {
    if ( reducedMotion || !UI_MOTION.enabled ) {
        return 0;
    }

    return ( index * UI_MOTION.staggerItemMs ) / 1000;
};

/**
 * Computes stagger delay for itinerary panel itinerary rows after a search completes.
 *
 * @param index Zero-based itinerary row index.
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Delay in seconds for Motion `transition.delay`.
 */
export const resolveUiItineraryPanelStagger = ( index: number, reducedMotion: boolean ): number => {
    if ( reducedMotion || !UI_MOTION.enabled ) {
        return 0;
    }

    return ( index * UI_MOTION.itineraryPanelStaggerItemMs ) / 1000;
};

/**
 * Computes reveal delay for a TravelProgress leg strip after itinerary search.
 *
 * @param segmentIndex Zero-based visible leg index within the itinerary row.
 * @param rowIndex Zero-based itinerary row index in the itinerary list.
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Delay in seconds for Motion `transition.delay`.
 */
export const resolveUiItineraryPanelProgressDelay = (
    segmentIndex: number,
    rowIndex: number,
    reducedMotion: boolean,
): number => {
    if ( reducedMotion || !UI_MOTION.enabled ) {
        return 0;
    }

    const rowOffsetMs = rowIndex * UI_MOTION.itineraryPanelProgressRowOffsetMs;
    const segmentOffsetMs = segmentIndex * UI_MOTION.itineraryPanelProgressSegmentStaggerMs;

    return ( rowOffsetMs + segmentOffsetMs ) / 1000;
};

/**
 * Whether UI motion animations should run.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns True when motion is enabled and not reduced.
 */
export const isUiMotionEnabled = ( reducedMotion: boolean ): boolean => {
    return UI_MOTION.enabled && !reducedMotion;
};
